import React from 'react';
import debounce from 'lodash/debounce';
import isEqual from 'lodash/isEqual';
import { withRouter, RouteComponentProps } from 'react-router-dom';
import { IBounds, IGeoLocation, IHotel } from 'src/common-types/index';
import { Map, openHotelPage, Responsive } from 'src/utils/index';
import MapMarker from 'src/assets/images/map-price-icon.svg';
import MapMarkerSelected from 'src/assets/images/map-price-selected-icon.svg';
import { SessionExpiredMessage } from 'src/components/components/index';
import { HotelCardComponent } from '../../result/hotel-card';
import './style.scss';

interface IProps extends RouteComponentProps {
  bounds?: IBounds;
  center?: google.maps.LatLngLiteral;
  locations: {
    location: IGeoLocation;
    id: number;
  }[];
  icon?: string;
  mapOptions?: google.maps.MapOptions;
  hotels?: IHotel[];
  nights?: number;
  withEvents?: boolean;
  selectedHotel?: IHotel;
  onSearchUpdate?: () => void;
  setBounds?: (bounds: IBounds) => void;
  disabled?: boolean;
  hideExpiredMessage?: boolean;
}

interface IState {
  selectedHotel: IHotel;
}

const zero = 0;
const debounceTime = 1000;

class MapWrapperComponent extends React.Component<IProps, IState> {
  mapRef: React.RefObject<HTMLDivElement> = React.createRef();
  map: google.maps.Map;
  state: IState;

  constructor(props: IProps) {
    super(props);

    this.state = {
      selectedHotel: props.selectedHotel ? props.selectedHotel : null,
    };
  }

  componentDidUpdate(prevProps: Readonly<IProps>): void {
    if (prevProps.hotels !== this.props.hotels) {
      if (this.props.hotels.some(({ hotelId }) => hotelId === this.props.selectedHotel?.hotelId)) {
        this.setState({ selectedHotel: this.props.selectedHotel });
      } else {
        this.setState({ selectedHotel: null });
      }

      Map.removeAllMarkers();
      this.setMarkers();
    }

    if (prevProps.bounds && this.props.bounds && !isEqual(prevProps.bounds, this.props.bounds)) {
      this.fitMapToBounds();
    }
  }

  selectHotel = (hotelId: number): void => {
    const { hotels = [] } = this.props;
    const selectedHotel = hotels.find((hotel: IHotel) => hotel.hotelId === hotelId);

    if (selectedHotel) {
      this.setState({ selectedHotel });

      const markers = document.querySelectorAll('.price-marker');

      if (markers && markers.length) {
        markers.forEach((marker: HTMLDivElement) => {
          marker.style.zIndex = '1';
          marker.style.background = `url(${MapMarker})`;
        });
      }

      const selectedMarker: HTMLDivElement = document.querySelector(
        `.price-marker[data-item-id="${hotelId}"]`,
      );

      if (selectedMarker) {
        selectedMarker.style.zIndex = '5';
        selectedMarker.style.background = `url(${MapMarkerSelected})`;
      }
    }
  };

  setMarkers = (): void => {
    const { locations, icon, selectedHotel } = this.props;

    Map.addMarkers(this.map, locations, icon ? icon : '', selectedHotel?.hotelId, this.selectHotel);
  };

  fitMapToBounds = () => {
    const bounds = this.map.getBounds();
    let mapBounds = null;

    if (bounds) {
      const sw = bounds.getSouthWest();
      const ne = bounds.getNorthEast();
      mapBounds = {
        northEast: {
          longitude: ne.lng(),
          latitude: ne.lat(),
        },
        southWest: {
          longitude: sw.lng(),
          latitude: sw.lat(),
        },
      };
    }

    if (!isEqual(this.props?.bounds, mapBounds)) {
      const bounds: google.maps.LatLngBounds = new google.maps.LatLngBounds(
        Map.getGoogleLocation(this.props.bounds.southWest),
        Map.getGoogleLocation(this.props.bounds.northEast),
      );

      this.map.fitBounds(bounds, zero);
    }
  };

  componentDidMount(): void {
    const { center, bounds = null, withEvents = false, mapOptions = {} } = this.props;

    if (this.mapRef && this.mapRef.current) {
      this.map = Map.initializeMap(this.mapRef.current, bounds ? undefined : center, mapOptions);

      if (bounds) {
        this.fitMapToBounds();
      }

      this.setMarkers();

      this.map.addListener('click', () => {
        if (this.state.selectedHotel) {
          this.setState({ selectedHotel: null });
        }
      });

      if (withEvents) {
        setTimeout(() => {
          this.map.addListener('center_changed', this.onCenterChange);
        }, debounceTime);
      }
    }
  }

  onCenterChange = debounce(() => {
    const bounds = this.map.getBounds();
    const sw = bounds.getSouthWest();
    const ne = bounds.getNorthEast();
    const resBounds = {
      northEast: {
        longitude: ne.lng(),
        latitude: ne.lat(),
      },
      southWest: {
        longitude: sw.lng(),
        latitude: sw.lat(),
      },
    };

    if (!isEqual(this.props?.bounds, resBounds)) {
      if (this.props.setBounds) {
        this.props.setBounds(resBounds);
      }

      if (this.props.onSearchUpdate) {
        this.props.onSearchUpdate();
      }
    }
  }, debounceTime);

  onHotelPage = (hotelId: number) => {
    openHotelPage(hotelId, this.props.history);
  };

  render(): React.ReactNode {
    const { selectedHotel } = this.state;
    const { nights = zero, disabled = false, hideExpiredMessage = false } = this.props;

    return (
      <div className="map-wrapper">
        {disabled && (
          <>
            {!hideExpiredMessage && (
              <div className="map-wrapper__expired-message">
                <SessionExpiredMessage onReset={() => window.location.reload()} />
              </div>
            )}
            <div className="map-wrapper__disabled-overlay" />
          </>
        )}
        <div className="map-wrapper__map">
          <div className="map-wrapper__map-instance" ref={this.mapRef} />
        </div>
        {selectedHotel && (
          <div
            className="map-wrapper__hotel-detail"
            onClick={() => this.onHotelPage(selectedHotel.hotelId)}
          >
            <Responsive>
              <HotelCardComponent
                hotel={selectedHotel}
                suppliers={[]}
                isFromSearch={false}
                nights={nights}
              />
            </Responsive>
          </div>
        )}
      </div>
    );
  }
}

export const MapWrapper = withRouter(MapWrapperComponent);
